/**
 * PortfolioTab — port of callbacks/position. Positions table + account summary.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { readBlotter } from '@/lib/apiErrors';
import { useStore } from '@/store';
import { getUnlockedBrokers, getUnlockedVault } from '@/lib/vault';
import { ORG, GRN, RED, MUT, FONT } from '@/theme';
import type { Position } from '@/types';

// Totals are NULL when any account could not be read (router: "a total that
// is missing a broker is a wrong number"); `degraded` names the broker.
interface AccountSummary {
  equity:        number | null;
  cash:          number | null;
  buy_power:     number | null;
  day_pnl:       number | null;
  day_pnl_pct:   number | null;
  total_pnl:     number | null;
  total_pnl_pct: number | null;
  degraded?:     Array<{ broker: string; error: string }>;
  partial?:      boolean;
}


// Null-safe money formatting. A broker can return a partial row (a halted /
// illiquid symbol with a null market_value, or a missing field on a partial
// payload), so a bare `.toFixed()` crashes the panel, and `?? 0` fabricates
// "$0" equity / buying power -- the exact "$0.00 on a failed read" the codebase
// forbids. Unknown renders as an em dash, never as zero.
const num    = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v)) ? v : NaN;
const money  = (v: unknown): string => { const n = num(v); return Number.isNaN(n) ? '—' : `$${n.toLocaleString()}`; };
const dol    = (v: unknown): string => { const n = num(v); return Number.isNaN(n) ? '—' : `$${n.toFixed(2)}`; };
const signed = (v: unknown): string => { const n = num(v); return Number.isNaN(n) ? '—' : `${n >= 0 ? '+' : ''}$${n.toFixed(2)}`; };
const pnl = (v: unknown, pct: unknown): string => {
  const n = num(v); if (Number.isNaN(n)) return '—';
  const p = num(pct);
  return `${n >= 0 ? '+' : ''}$${n.toFixed(2)}${Number.isNaN(p) ? '' : ` (${p >= 0 ? '+' : ''}${p.toFixed(2)}%)`}`;
};

// An unknown P&L is uncoloured, not red (and never green).
const pnlColor = (v: unknown): string | undefined => {
  const n = num(v); return Number.isNaN(n) ? undefined : (n >= 0 ? GRN : RED);
};

export function PortfolioTab() {
  const setFocus = useStore(s => s.setFocus);

  // WHOSE MONEY IS ON SCREEN? api() attaches the unlocked vault's broker
  // credentials automatically; with the vault LOCKED the same endpoints resolve
  // against the SHARED PLATFORM account. Every other money surface (DomTab,
  // OrderTicket) says which one it is -- this panel, the one that prints EQUITY,
  // CASH, BUY PWR, DAY P&L and your positions, said nothing at all. Key the
  // queries on the vault identity too, so switching accounts cannot render the
  // previous account's numbers from cache.
  const vaultAcct = getUnlockedVault();
  // Every unlocked entry, not just the active one: the router reads ALL of
  // them (one per broker), so unlocking a second broker changes the answer.
  const acctScope = Object.keys(getUnlockedBrokers()).sort().join(',') || 'locked';

  const { data: acct, isError: acctError, error: acctErr } = useQuery<AccountSummary>({
    queryKey: ['acct', acctScope], queryFn: () => api('/api/portfolio/account'), refetchInterval: 30_000,
  });
  // The router answers a plain array, or { rows, degraded, partial } when a
  // broker could not be read. Typed as Position[], the partial shape crashed
  // this table; readBlotter throws on any other shape rather than show "flat".
  const { data: posBlotter, isError: posError, error: posErr, isLoading: posLoading } = useQuery({
    queryKey: ['positions', acctScope],
    queryFn: async () => readBlotter<Position>(await api<unknown>('/api/portfolio')),
    refetchInterval: 30_000,
  });
  const positions = posBlotter?.rows;
  const posDegraded = posBlotter?.degraded ?? [];

  return (
    <div style={{ fontFamily: FONT, fontSize: 11 }}>
      {/* The router holds NO platform brokerage account any more (D14): with
          the vault locked it refuses these reads (OWN_ACCOUNT_REQUIRED), it does
          not answer with a shared account. Say that, not "shared platform". */}
      {!vaultAcct && (
        <div style={{ border: '1px solid #c08a3e', background: 'rgba(192,138,62,0.10)', padding: '5px 8px', marginBottom: 8, color: '#c08a3e', fontSize: 10, lineHeight: 1.5 }}>
          BROKER VAULT LOCKED — there is no account to show. Unlock the vault to see your
          own balances and positions.
        </div>
      )}
      {vaultAcct && (
        <div style={{ fontSize: 9, color: MUT, letterSpacing: 0.5, marginBottom: 6 }}>
          ACCOUNTS: every broker unlocked in your vault (your own)
        </div>
      )}
      {(acct?.degraded?.length ?? 0) > 0 && (
        <div style={{ color: '#c08a3e', fontSize: 10, marginBottom: 6 }}>
          PARTIAL — could not read {acct!.degraded!.map(d => `${d.broker} (${d.error})`).join(', ')}.
          Totals are withheld (a total missing a broker is a wrong number).
        </div>
      )}
      {/* Account summary strip. On a failed read we say so -- we do NOT hide the
          strip (which reads as "flat") or print $0 buying power. */}
      {acct ? (
        <div style={{ display: 'flex', gap: 18, marginBottom: 10, padding: '6px 4px', borderBottom: '1px solid #222' }}>
          <Stat label="EQUITY"    value={money(acct.equity)} />
          <Stat label="CASH"      value={money(acct.cash)} />
          <Stat label="BUY PWR"   value={money(acct.buy_power)} />
          <Stat label="DAY P&L"   value={pnl(acct.day_pnl, acct.day_pnl_pct)}     color={pnlColor(acct.day_pnl)} />
          <Stat label="TOTAL P&L" value={pnl(acct.total_pnl, acct.total_pnl_pct)} color={pnlColor(acct.total_pnl)} />
        </div>
      ) : acctError ? (
        <div style={{ marginBottom: 10, padding: '6px 4px', borderBottom: '1px solid #222', color: RED }}>
          ACCOUNT UNAVAILABLE — {(acctErr as Error | null)?.message ?? 'the read did not complete'} (this is not $0)
        </div>
      ) : null}

      {posDegraded.length > 0 && (positions?.length ?? 0) > 0 && (
        <div style={{ color: '#c08a3e', fontSize: 10, marginBottom: 6 }}>
          PARTIAL — positions at {posDegraded.map(d => `${d.broker} (${d.error})`).join(', ')} could NOT be read and are not shown.
        </div>
      )}
      {/* Positions table */}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ color: ORG, fontSize: 9, letterSpacing: 1 }}>
            <th style={th}>Symbol</th>
            <th style={th}>Side</th>
            <th style={th}>Qty</th>
            <th style={th}>Avg Cost</th>
            <th style={th}>Mkt Value</th>
            <th style={th}>Unrl P&L</th>
          </tr>
        </thead>
        <tbody>
          {(positions ?? []).map(p => (
            <tr key={p.symbol} onClick={() => setFocus(p.symbol)} style={{ cursor: 'pointer' }}>
              <td style={{ ...td, color: ORG, fontWeight: 'bold' }}>{p.symbol}</td>
              <td style={td}>{String(p.side ?? '—').toUpperCase()}</td>
              <td style={td}>{p.qty ?? '—'}</td>
              <td style={td}>{dol(p.avg_cost)}</td>
              <td style={td}>{dol(p.market_value)}</td>
              <td style={{ ...td, color: pnlColor(p.unrealized_pl) }}>{signed(p.unrealized_pl)}</td>
            </tr>
          ))}
          {posError && positions == null && (
            <tr><td colSpan={6} style={{ ...td, color: RED, textAlign: 'center', padding: 20 }}>
              POSITIONS UNAVAILABLE — {(posErr as Error | null)?.message ?? 'fetch failed'} (this is NOT "flat")
            </td></tr>
          )}
          {posLoading && positions == null && (
            <tr><td colSpan={6} style={{ ...td, color: MUT, textAlign: 'center', padding: 20 }}>LOADING…</td></tr>
          )}
          {!posError && !posLoading && (!positions || positions.length === 0) && (
            <tr><td colSpan={6} style={{ ...td, color: MUT, textAlign: 'center', padding: 20 }}>
              NO OPEN POSITIONS{posDegraded.length ? ` at the brokers that answered -- ${posDegraded.map(d => d.broker).join(', ')} could NOT be read` : ''}
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div>
      <div style={{ color: MUT, fontSize: 9, letterSpacing: 1 }}>{label}</div>
      <div style={{ color: color ?? '#e0e0e0', fontSize: 12, fontWeight: 'bold' }}>{value}</div>
    </div>
  );
}

const th: React.CSSProperties = { padding: '3px 6px', textAlign: 'right', borderBottom: '1px solid #222' };
const td: React.CSSProperties = { padding: '3px 6px', textAlign: 'right', borderBottom: '1px solid #161616', color: '#e0e0e0' };
